import { useAppContext } from "@/hooks/useAppContext";
import { BaseDialog } from "@/components/ui/base-dialog";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ScryfallCard } from "@/types/scryfall";
import { Card, CardContent } from "@/components/ui/card";
import {
  Crown,
  Sparkles,
  Wand2,
  Star,
  ChevronRight,
  ChevronLeft,
  Check
} from 'lucide-react';

import { SimplifiedCommanderStep } from "./deck-builder/SimplifiedCommanderStep";
import { SimplifiedStrategyStep } from "./deck-builder/SimplifiedStrategyStep";
import { SimplifiedCardSelectionStep } from "./deck-builder/SimplifiedCardSelectionStep";
import { InitialStep } from "./deck-builder/InitialStep";
import { KindredStep } from "./deck-builder/KindredStep";
import { TypalStep } from "./deck-builder/TypalStep";
import { KINDRED_OPTIONS, buildKindredQuery, buildSimpleQuery } from "./deck-builder/utils";

interface DeckBuilderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  addCard: (card: ScryfallCard) => void;
  onFinish: (query: string, cards: ScryfallCard[], order: string, dir: string, commander?: ScryfallCard) => void;
}

export const DeckBuilderModal = ({ open, onOpenChange, addCard, onFinish }: DeckBuilderModalProps) => {
  const { config } = useAppContext();
  const { scryfallApiUrl } = config;

  // Step management
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<'guided' | 'manual' | null>(null);

  // Commander step state
  const [commander, setCommander] = useState<ScryfallCard | null>(null);
  const [colorIdentity, setColorIdentity] = useState<string[]>([]);

  // Strategy step state
  const [themes, setThemes] = useState('');
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [creatureTypes, setCreatureTypes] = useState<string[]>([]);

  // Card selection step state
  const [suggestedCards, setSuggestedCards] = useState<ScryfallCard[]>([]);
  const [selectedCards, setSelectedCards] = useState<ScryfallCard[]>([]);
  const [suggestedCommanders, setSuggestedCommanders] = useState<ScryfallCard[]>([]);

  // UI state
  const [isFinishing, setIsFinishing] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<string>("");

  const handleNextStep = async (
    strategyOverride?: string,
    colorIdentityOverride?: string[],
    themesOverride?: string,
    creatureTypeOverride?: string,
    cardTypeOverride?: string
  ) => {
    const currentStrategy = strategyOverride || selectedStrategy;
    if (step === 2 && currentStrategy === 'kindred') {
      setStep(2.5); // Special step for Kindred
      return;
    }
    if (step === 2 && currentStrategy === 'typal') {
      setStep(2.6); // Special step for Typal
      return;
    }

    if (step === 2 || step === 2.5 || step === 2.6) {
      if (mode === 'guided') {
        setSelectedCards([]);
        setSuggestedCommanders([]);
      }
      setIsFetchingSuggestions(true);

      const fetchCardSuggestions = async () => {
        const effectiveCreatureTypes = creatureTypeOverride ? [creatureTypeOverride] : creatureTypes;
        const effectiveCardTypes = cardTypeOverride ? cardTypeOverride.split(" ") : cardTypes;
        let effectiveColorIdentity = colorIdentityOverride || colorIdentity;

        // For kindred decks, ensure we use the kindred's specific color identity
        if (currentStrategy === 'kindred' && effectiveCreatureTypes.length > 0) {
          const kindred = KINDRED_OPTIONS.find(k => k.creatureType === effectiveCreatureTypes[0]);
          if (kindred) {
            effectiveColorIdentity = kindred.colorIdentity;
          }
        }

        const query = currentStrategy === 'kindred' && effectiveCreatureTypes.length > 0
          ? buildKindredQuery(effectiveColorIdentity, effectiveCreatureTypes[0])
          : buildSimpleQuery(effectiveColorIdentity, themesOverride || themes, effectiveCardTypes, effectiveCreatureTypes);
        try {
          const response = await fetch(`${scryfallApiUrl}/cards/search?q=${encodeURIComponent(query)}&order=edhrec&dir=asc`, {
            referrerPolicy: 'no-referrer',
          });
          const data = await response.json();
          setSuggestedCards(data.data || []);
        } catch (error) {
          console.error("Failed to fetch card suggestions:", error);
          setSuggestedCards([]);
        }
      };

      const fetchSuggestedCommanders = async () => {
        if (!commander) {
          try {
            const effectiveCreatureTypes = creatureTypeOverride ? [creatureTypeOverride] : creatureTypes;
            const effectiveCardTypes = cardTypeOverride ? cardTypeOverride.split(" ") : cardTypes;
            const effectiveColorIdentity = colorIdentityOverride || colorIdentity;

            // For kindred decks, always use the kindred's specific color identity
            let commanderColors = effectiveColorIdentity;
            if (currentStrategy === 'kindred' && effectiveCreatureTypes.length > 0) {
              const kindred = KINDRED_OPTIONS.find(k => k.creatureType === effectiveCreatureTypes[0]);
              if (kindred) {
                commanderColors = kindred.colorIdentity;
                setColorIdentity(kindred.colorIdentity);
              }
            }

            let themedQuery = currentStrategy === 'kindred' && effectiveCreatureTypes.length > 0
              ? buildKindredQuery(commanderColors, effectiveCreatureTypes[0], true)
              : buildSimpleQuery(commanderColors, themesOverride || themes, effectiveCardTypes, effectiveCreatureTypes, true);
            themedQuery += ` is:commander`;

            const themedResponse = await fetch(`${scryfallApiUrl}/cards/search?q=${encodeURIComponent(themedQuery)}&order=edhrec&dir=asc`, {
              referrerPolicy: 'no-referrer',
            });
            const themedData = await themedResponse.json();
            let suggestions = themedData.data || [];

            if (suggestions.length < 3) {
              const needed = 3 - suggestions.length;
              let fallbackQuery = 'is:commander f:edh game:paper usd<=5';
              if (commanderColors.length > 0) {
                fallbackQuery += ` ci=${commanderColors.join('')}`;
              }

              const fallbackResponse = await fetch(`${scryfallApiUrl}/cards/search?q=${encodeURIComponent(fallbackQuery)}&order=edhrec&dir=asc&unique=cards`, {
                referrerPolicy: 'no-referrer',
              });
              const fallbackData = await fallbackResponse.json();
              const fallbackSuggestions = fallbackData.data || [];

              const existingIds = new Set(suggestions.map(c => c.id));
              const additionalCommanders = fallbackSuggestions.filter(c => !existingIds.has(c.id));

              suggestions = [...suggestions, ...additionalCommanders.slice(0, needed)];
            }

            setSuggestedCommanders(suggestions.slice(0, 3));
          } catch (error) {
            console.error("Failed to fetch suggested commanders:", error);
            setSuggestedCommanders([]);
          }
        }
      };

      await Promise.all([fetchCardSuggestions(), fetchSuggestedCommanders()]);

      setIsFetchingSuggestions(false);
      setStep(3);
    } else {
      setStep(step + 1);
    }
  };

  const handleFinish = async () => {
    setIsFinishing(true);

    await new Promise(resolve => setTimeout(resolve, 1000));

    const finalCards = [...selectedCards];
    if (commander) {
      finalCards.push(commander);
    }
    finalCards.forEach(card => addCard(card));

    const query = buildSimpleQuery(colorIdentity, themes, cardTypes, creatureTypes);

        onFinish(query, suggestedCards, 'edhrec', 'asc', commander || undefined);
    onOpenChange(false);
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <InitialStep
            onModeSelect={(selectedMode) => {
              setMode(selectedMode);
              if (selectedMode === 'guided') {
                setStep(2);
              } else {
                setStep(1);
              }
            }}
          />
        );
      case 1:
        return (
          <SimplifiedCommanderStep
            commander={commander}
            setCommander={setCommander}
            setColorIdentity={setColorIdentity}
            colorIdentity={colorIdentity}
            scryfallApiUrl={scryfallApiUrl}
          />
        );
      case 2:
        return (
          <SimplifiedStrategyStep
            themes={themes}
            setThemes={setThemes}
            cardTypes={cardTypes}
            setCardTypes={setCardTypes}
            creatureTypes={creatureTypes}
            setCreatureTypes={setCreatureTypes}
            isGuided={mode === 'guided'}
            setColorIdentity={setColorIdentity}
            handleNextStep={handleNextStep}
            setSelectedStrategy={setSelectedStrategy}
          />
        );
      case 2.5:
        return (
          <KindredStep
            setCreatureTypes={setCreatureTypes}
            setColorIdentity={setColorIdentity}
            handleNextStep={handleNextStep}
          />
        );
      case 2.6:
        return (
          <TypalStep
            setCardTypes={setCardTypes}
            setColorIdentity={setColorIdentity}
            handleNextStep={handleNextStep}
          />
        );
      case 3:
        return (
          <SimplifiedCardSelectionStep
            commander={commander}
            colorIdentity={colorIdentity}
            themes={themes}
            suggestedCards={suggestedCards}
            setSuggestedCards={setSuggestedCards}
            selectedCards={selectedCards}
            setSelectedCards={setSelectedCards}
            scryfallApiUrl={scryfallApiUrl}
            suggestedCommanders={suggestedCommanders}
            setCommander={setCommander}
            cardTypes={cardTypes}
            creatureTypes={creatureTypes}
          />
        );
      default:
        return (
          <InitialStep
            onModeSelect={(selectedMode) => {
              setMode(selectedMode);
              if (selectedMode === 'guided') {
                setStep(2);
              } else {
                setStep(1);
              }
            }}
          />
        );
    }
  };

  const getTitle = () => {
    if (step === 0) return <span className="flex items-center justify-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Deck Builder</span>;
    if (step === 1) return <span className="flex items-center justify-center gap-2"><Crown className="w-5 h-5 text-primary" />Deck Builder</span>;
    if (step === 2) return <span className="flex items-center justify-center gap-2"><Wand2 className="w-5 h-5 text-primary" />Deck Builder</span>;
    if (step === 2.5) return <span className="flex items-center justify-center gap-2"><Wand2 className="w-5 h-5 text-primary" />Deck Builder</span>;
    if (step === 3) return <span className="flex items-center justify-center gap-2"><Star className="w-5 h-5 text-primary" />Deck Builder</span>;
    return <span className="flex items-center justify-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Deck Builder</span>;
  };

  const getDescription = () => {
    if (step === 0) return "Let's build your perfect Magic deck together!";
    if (step === 1) return "Let's build your perfect Magic deck together!";
    if (step === 2) return "Let's build your perfect Magic deck together!";
    if (step === 2.5) return "Let's build your perfect Magic deck together!";
    if (step === 3) return "Let's build your perfect Magic deck together!";
    return "Let's build your perfect Magic deck together!";
  };

  const canProceed = () => {
    if (step === 0) return mode !== null;
    if (step === 1) return commander || colorIdentity.length > 0;
    if (step === 2) return true;
    if (step === 2.5) return creatureTypes.length > 0;
    if (step === 3) return true;
    return false;
  };

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep(0);
      setMode(null);
      setCommander(null);
      setColorIdentity([]);
      setThemes('');
      setCardTypes([]);
      setCreatureTypes([]);
      setSuggestedCards([]);
      setSelectedCards([]);
      setSuggestedCommanders([]);
      setIsFinishing(false);
      setSelectedStrategy("");
    }
  }, [open]);

  return (
    <BaseDialog
      isOpen={open}
      onOpenChange={onOpenChange}
      size="deck-builder"
      title={<span className='font-semibold text-center text-base md:text-lg'>{getTitle()}</span>}
      description={<span className='text-muted-foreground text-sm text-center'>{getDescription()}</span>}
      headerClassName='px-4 md:px-6 pt-4 md:pt-6 pb-1 relative flex-shrink-0'
      contentClassName='flex flex-col max-h-[90vh] bg-background border-border'
    >
      <div className='px-4 md:px-6 pt-2 pb-4 space-y-4 overflow-y-auto flex-1'>
        {/* Progress Indicator */}
        {step > 0 && (
          <div className="space-y-2 md:space-y-3">
            <div className="flex justify-center">
              <div className="flex items-center gap-1 md:gap-2">
                {[1, 2, 3].map((stepNumber) => (
                  <div key={stepNumber} className="flex items-center">
                    <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium transition-all ${
                      stepNumber === Math.floor(step)
                        ? 'bg-primary text-white'
                        : stepNumber < step
                          ? 'bg-primary/20 text-primary'
                          : 'bg-muted text-muted-foreground'
                    }`}>
                      {stepNumber < step ? <Check className="w-3 h-3 md:w-4 md:h-4" /> : stepNumber}
                    </div>
                    {stepNumber < 3 && (
                      <div className={`w-4 md:w-8 h-0.5 mx-1 md:mx-2 ${
                        stepNumber < step ? 'bg-primary' : 'bg-muted'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center text-xs md:text-sm text-muted-foreground px-2">
              {step === 1 && "Step 1: Choose your commander or colors"}
              {step === 2 && "Step 2: Pick your strategy"}
              {step === 2.5 && "Step 2.5: Choose your Kindred"}
              {step === 2.6 && "Step 2.5: Choose your Typal Focus"}
              {step === 3 && "Step 3: Add some cards"}
            </div>
          </div>
        )}

        {/* Step Content */}
        {renderStep()}

        {/* Finishing State */}
        {isFinishing && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
            <Card className="p-6 max-w-sm mx-auto">
              <CardContent className="text-center space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"></div>
                  <Sparkles className="absolute inset-0 w-8 h-8 text-primary animate-pulse m-auto" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Building Your Deck...</h4>
                  <p className="text-sm text-muted-foreground">
                    Adding your selected cards and setting up the perfect search
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 md:px-6 py-3 md:py-4 border-t border-border bg-muted/20 flex-shrink-0">
        {step > 0 && (
          <div className="flex justify-center items-center gap-2 md:gap-4">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => {
                  if (step === 2.5) {
                    setCreatureTypes([]);
                    setStep(2);
                  } else if (step === 2.6) {
                    setCardTypes([]);
                    setStep(2);
                  } else if (mode === 'guided' && step === 2) {
                    setStep(0);
                  } else if (mode === 'manual' && step === 2) {
                    setThemes('');
                    setCardTypes([]);
                    setCreatureTypes([]);
                    setSelectedStrategy("");
                    setStep(1);
                  } else if (step === 1) {
                    setMode(null);
                    setStep(0);
                  } else if (step > 0) {
                    if (step === 3) {
                      setSuggestedCards([]);
                      setSelectedCards([]);
                      setSuggestedCommanders([]);
                    }
                    setStep(step - 1);
                  }
                }}
                disabled={isFinishing}
                className="flex items-center gap-1 md:gap-2 text-sm md:text-base"
                size="sm"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
            )}

            {step < 3 && (mode !== 'guided' || step !== 2) && (
              <Button
                onClick={() => handleNextStep()}
                disabled={!canProceed() || isFinishing || isFetchingSuggestions}
                className="flex items-center gap-1 md:gap-2 bg-primary hover:bg-primary/90 px-4 md:px-8 text-sm md:text-base"
                size="sm"
              >
                {isFetchingSuggestions ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span className="hidden sm:inline">Loading Cards...</span>
                    <span className="sm:hidden">Loading...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">
                      {step === 1 ? "Let's Pick a Strategy!" : step === 2.5 ? "Show Me Cards!" : "Next"}
                    </span>
                    <span className="sm:hidden">
                      {step === 1 ? "Pick Strategy" : step === 2.5 ? "Show Cards" : "Next"}
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}

            {step === 3 && (
              <Button
                onClick={handleFinish}
                disabled={isFinishing}
                className="flex items-center gap-1 md:gap-2 bg-primary hover:bg-primary/90 px-4 md:px-8 text-sm md:text-base"
                size="sm"
              >
                {isFinishing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span className="hidden sm:inline">Building Your Deck...</span>
                    <span className="sm:hidden">Building...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span className="hidden sm:inline">Start Building My Deck!</span>
                    <span className="sm:hidden">Build Deck!</span>
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </BaseDialog>
  );
};